/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import hljs from 'cio/utils/highlightjs';

interface CodeSnippetArgs {
  language?: string;
  highlightCode?: boolean;
  showLineNumbers?: boolean;
  snippet?: string;
  snippetClass?: string;
  theme?: 'light' | 'dark';
  preventWheelEventPropagation?: boolean;
}

export default class CodeSnippet extends Component<CodeSnippetArgs> {
  @tracked highlightedHtmlString: string | null | undefined = '';
  @tracked snippetFromContent: string | null = null;

  get highlightCode() {
    return this.args.highlightCode ?? true;
  }

  get showLineNumbers() {
    return this.args.showLineNumbers ?? false;
  }

  get theme() {
    return this.args.theme ?? 'light';
  }

  get themeClass() {
    if (this.theme === 'dark') {
      return 'hljs-dark';
    }

    return 'hljs-light';
  }

  get languageClass() {
    if (this.args.language == null) {
      return;
    }

    return `language-${this.args.language}`;
  }

  get lineNumbers() {
    if (this.args.snippet != null) {
      return this.lines(this.args.snippet).join('\n');
    }

    if (this.snippetFromContent != null) {
      return this.lines(this.snippetFromContent).join('\n');
    }

    return null;
  }

  flexContainer: HTMLElement | null = null;

  @action
  didInsert(element: HTMLElement) {
    this.flexContainer = element;

    if (this.args.preventWheelEventPropagation) {
      this.flexContainer.addEventListener('wheel', this.onWheel);
    }
  }

  willDestroy() {
    super.willDestroy();

    if (this.flexContainer != null && this.args.preventWheelEventPropagation) {
      this.flexContainer.removeEventListener('wheel', this.onWheel);
    }
  }

  // This wheel event is a hack to prevent the scroll-lock modifier on FlyModal
  // from breaking scrolling in the CodeSnippet component when it's embedded in
  // a modal
  @action
  onWheel(event: MouseEvent) {
    event.stopPropagation();
  }

  @action
  highlight(element: HTMLPreElement) {
    if (!this.highlightCode) {
      this.highlightedHtmlString = this.args.snippet;
      return;
    }

    if (this.args.snippet != null) {
      if (this.args.language != null) {
        this.highlightedHtmlString = hljs.highlight(this.args.snippet, { language: this.args.language }).value;
      } else {
        this.highlightedHtmlString = hljs.highlightAuto(this.args.snippet).value;
      }
      return;
    }

    this.snippetFromContent = element.textContent;
    hljs.highlightElement(element);
  }

  // Create newline separated line numbers for the snippet
  private lines(snippet: string) {
    return snippet.split('\n').map((_, i) => `${i + 1}`);
  }
}
